import {useEffect, useState} from 'react';
import {useParams, Link} from 'react-router-dom';
import {Button, Spin, Typography, Collapse} from 'antd';

import {Plan, Question, Test} from '../../graphql/API';
import {PlansService} from './PlansService';
import {QuestionsService} from '../Questions';
import Title from 'antd/es/typography/Title';

const plansService = new PlansService();
const questionsService = new QuestionsService();

export const PlanDetails = () => {
  const [plan, setPlan] = useState<any | undefined>(undefined);
  const [isError, setIsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // State for lazy-loaded daily exercises
  const [loadedDayExercises, setLoadedDayExercises] = useState<
    Map<number, any[]>
  >(new Map());
  const [loadingDays, setLoadingDays] = useState<Set<number>>(new Set());

  let params: {planId: string} = useParams();

  const {Panel} = Collapse;
  const {Title, Text} = Typography;

  // OLD CODE - Commented out (kept for reference)
  // useEffect(() => {
  //   plansService
  //     .getPlanById(params.planId)
  //     .then((resp) => {
  //       setIsLoading(false);
  //       setPlan(resp);
  //     })
  //     .catch((error) => {
  //       setIsLoading(false);
  //       setIsError(true);
  //       console.log('getPlanById Error ->', error);
  //     });
  // }, []);

  useEffect(() => {
    console.log('🔍 [PLAN-DETAILS] Fetching plan by id:', params.planId);
    plansService
      .getNewPlanById(params.planId)
      .then(resp => {
        console.log('✅ [PLAN-DETAILS] Plan fetched:', resp);
        setIsLoading(false);
        setPlan(resp);
      })
      .catch(error => {
        setIsLoading(false);
        setIsError(true);
        console.error('❌ [PLAN-DETAILS] Error fetching plan:', error);
      });
  }, [params.planId]);

  // Load exercises for a specific day
  const loadDayExercises = async (dayNumber: number) => {
    // Check if already loaded
    if (loadedDayExercises.has(dayNumber)) {
      console.log(
        '✅ [PLAN-DETAILS] Day',
        dayNumber,
        'exercises already loaded (cached)',
      );
      return;
    }

    // Check if already loading
    if (loadingDays.has(dayNumber)) {
      console.log(
        '⏳ [PLAN-DETAILS] Day',
        dayNumber,
        'exercises already loading...',
      );
      return;
    }

    console.log('🔍 [PLAN-DETAILS] Loading exercises for day:', dayNumber);
    setLoadingDays(new Set(loadingDays).add(dayNumber));

    try {
      const exercises = await plansService.getDailyExercisesByPlanAndDay(
        params.planId,
        dayNumber,
      );
      console.log(
        '✅ [PLAN-DETAILS] Exercises loaded for day',
        dayNumber,
        ':',
        exercises,
      );

      // Populate question data for each exercise
      console.log(
        '🔍 [PLAN-DETAILS] Populating question data for exercises...',
      );
      for (const exercise of exercises) {
        if (
          exercise.questions &&
          Array.isArray(exercise.questions) &&
          exercise.questions.length > 0
        ) {
          const questionIds = exercise.questions
            .map((q: any) => q._id || q.id)
            .filter((id: string) => id); // Filter out any undefined/null IDs

          if (questionIds.length > 0) {
            console.log(
              `🔍 [PLAN-DETAILS] Fetching ${questionIds.length} questions for exercise ${exercise.name}`,
            );

            // Fetch all questions in parallel
            const fullQuestions = await Promise.all(
              questionIds.map((id: string) =>
                questionsService.getQuestionById(id).catch(err => {
                  console.warn(
                    `⚠️ [PLAN-DETAILS] Failed to fetch question ${id}:`,
                    err,
                  );
                  return null;
                }),
              ),
            );

            // Filter out any null results (failed fetches) and replace question data
            exercise.questions = fullQuestions.filter(q => q !== null);
            console.log(
              `✅ [PLAN-DETAILS] Populated ${exercise.questions.length} questions for exercise ${exercise.name}`,
            );
          }
        }
      }

      // Update loaded exercises
      setLoadedDayExercises(
        new Map(loadedDayExercises).set(dayNumber, exercises),
      );

      // Remove from loading set
      const newLoadingDays = new Set(loadingDays);
      newLoadingDays.delete(dayNumber);
      setLoadingDays(newLoadingDays);
    } catch (error) {
      console.error(
        '❌ [PLAN-DETAILS] Error loading exercises for day',
        dayNumber,
        ':',
        error,
      );

      // Remove from loading set
      const newLoadingDays = new Set(loadingDays);
      newLoadingDays.delete(dayNumber);
      setLoadingDays(newLoadingDays);
    }
  };
  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>
        Plan: {plan?.name || 'Loading...'}
      </Title>
      {plan && (
        <div style={{marginBottom: 20}}>
          <Text style={{display: 'block'}}>
            <Text style={{fontWeight: 'bold'}}>Duration: </Text>
            {plan.duration || 'N/A'} days
          </Text>
          <Text style={{display: 'block'}}>
            <Text style={{fontWeight: 'bold'}}>Level: </Text>
            {typeof plan.level === 'object'
              ? plan.level?.name
              : plan.level || 'N/A'}
          </Text>
          <Text style={{display: 'block'}}>
            <Text style={{fontWeight: 'bold'}}>Grade: </Text>
            {typeof plan.grade === 'object'
              ? plan.grade?.name
              : plan.grade || 'N/A'}
          </Text>
          <Text style={{display: 'block'}}>
            <Text style={{fontWeight: 'bold'}}>Curriculum: </Text>
            {typeof plan.curriculum === 'object'
              ? plan.curriculum?.name
              : plan.curriculum || 'N/A'}
          </Text>
        </div>
      )}
      <Button>
        <Link
          // @ts-ignore
          to={{
            pathname: '/plans/' + plan?.id + '/edit',
            state: {plan: plan},
          }}>
          Edit
        </Link>
      </Button>
      {!isError && !isLoading && plan && (
        <>
          <Collapse
            onChange={activeKeys => {
              // Load exercises for expanded panels
              if (Array.isArray(activeKeys)) {
                activeKeys.forEach(key => {
                  const dayNumber = Number(key);
                  if (!isNaN(dayNumber)) {
                    loadDayExercises(dayNumber);
                  }
                });
              }
            }}>
            {Array.from({length: plan.duration || 0}, (_, index) => {
              const dayNumber = index;
              const dayExercises = loadedDayExercises.get(dayNumber) || [];
              const isLoadingDay = loadingDays.has(dayNumber);

              return (
                <Panel
                  header={`Day ${dayNumber + 1}`}
                  key={dayNumber}
                  extra={
                    <Text type="secondary">
                      {isLoadingDay
                        ? 'Loading...'
                        : `${dayExercises.length} exercise(s)`}
                    </Text>
                  }>
                  {isLoadingDay ? (
                    <Spin tip="Loading exercises..." />
                  ) : dayExercises.length > 0 ? (
                    dayExercises.map((exercise: any, exerciseIndex: number) => {
                      const exerciseId = exercise?.id || exercise?._id;

                      return (
                        <Collapse key={exerciseIndex} defaultActiveKey={['1']}>
                          <Panel
                            key={exerciseId || exerciseIndex}
                            header={`Exercise: ${exercise.name || 'Unnamed'}`}>
                            <Text style={{display: 'block'}}>
                              <Text style={{fontWeight: 'bold'}}>
                                Description:{' '}
                              </Text>
                              {exercise.description || 'N/A'}
                            </Text>
                            <Text style={{display: 'block'}}>
                              <Text style={{fontWeight: 'bold'}}>
                                Summary:{' '}
                              </Text>
                              {exercise.summary || 'N/A'}
                            </Text>
                            <Text style={{display: 'block'}}>
                              <Text style={{fontWeight: 'bold'}}>Type: </Text>
                              {exercise.type || 'N/A'}
                            </Text>
                            {exercise.thumbnail && (
                              <a
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{display: 'block'}}
                                href={exercise.thumbnail}>
                                Thumbnail url
                              </a>
                            )}
                            {exercise.media && exercise.media.length > 0 && (
                              <div style={{marginTop: 10}}>
                                <Text
                                  style={{
                                    fontWeight: 'bold',
                                    display: 'block',
                                  }}>
                                  Media:
                                </Text>
                                {exercise.media.map(
                                  (media: any, mIndex: number) => (
                                    <a
                                      key={mIndex}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{display: 'block'}}
                                      href={media.uri || media.url}>
                                      Media {mIndex + 1}
                                    </a>
                                  ),
                                )}
                              </div>
                            )}
                            {exercise.questions &&
                              exercise.questions.length > 0 && (
                                <Collapse
                                  defaultActiveKey={['1']}
                                  style={{marginTop: 10}}>
                                  {exercise.questions.map(
                                    (question: any, qIndex: number) => (
                                      <Panel
                                        key={question?.id || qIndex}
                                        header={`Question: ${question?.name || question?.text || 'Unnamed'}`}>
                                        <Text style={{display: 'block'}}>
                                          <Text style={{fontWeight: 'bold'}}>
                                            Text:{' '}
                                          </Text>
                                          {question?.text || 'N/A'}
                                        </Text>
                                        <Text style={{display: 'block'}}>
                                          <Text style={{fontWeight: 'bold'}}>
                                            Summary:{' '}
                                          </Text>
                                          {question?.summary || 'N/A'}
                                        </Text>
                                        <Text style={{display: 'block'}}>
                                          <Text style={{fontWeight: 'bold'}}>
                                            Description:{' '}
                                          </Text>
                                          {question?.description || 'N/A'}
                                        </Text>
                                      </Panel>
                                    ),
                                  )}
                                </Collapse>
                              )}
                          </Panel>
                        </Collapse>
                      );
                    })
                  ) : (
                    <Text type="secondary">No exercises for this day</Text>
                  )}
                </Panel>
              );
            })}
          </Collapse>
        </>
      )}
      {isLoading && (
        <Spin size="large" style={{margin: 'auto', width: '100%'}} />
      )}
      {isError && <Title>Plan fetching error ⚠️</Title>}
    </div>
  );
};
